import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Monitor,
  Plus,
  Copy,
  RefreshCw,
  Trash2,
  Edit2,
  Clock,
  Loader2,
  QrCode,
  Check,
  KeyRound,
  Search,
  Camera,
  ChevronLeft,
  Wifi,
  WifiOff,
  ArrowUpRight,
  FileUp,
  Settings2,
  Palette,
  Upload,
  X,
  ImageIcon,
  ChevronDown,
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import KioskDataImport from '@/pages/checkin/KioskDataImport';
import { ImageCropper } from '@/components/ImageCropper';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generatePairingCode() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 6; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
}

function formatLastSeen(timestamp) {
  if (!timestamp) return 'Never';
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}

function getStatusBadge(status) {
  switch (status) {
    case 'active':
      return { className: 'bg-green-100 text-green-700 border-green-300', label: 'Active' };
    case 'pending':
      return { className: 'bg-amber-100 text-amber-700 border-amber-300', label: 'Pending' };
    case 'revoked':
      return { className: 'bg-red-100 text-red-700 border-red-300', label: 'Revoked' };
    default:
      return { className: '', label: status };
  }
}

const AUTH_MODES = [
  { value: 'both', label: 'PIN + Name Search' },
  { value: 'pin_only', label: 'PIN Only' },
  { value: 'search_only', label: 'Name Search Only' },
];

function getAuthMode(settings) {
  const pin = settings?.require_pin !== false;
  const search = settings?.allow_name_search !== false;
  if (pin && search) return 'both';
  if (pin && !search) return 'pin_only';
  if (!pin && search) return 'search_only';
  return 'both';
}

function getAuthModeLabel(settings) {
  const mode = getAuthMode(settings);
  return AUTH_MODES.find((m) => m.value === mode)?.label || 'PIN + Name Search';
}

// ---------------------------------------------------------------------------
// Image helpers for kiosk icon upload
// ---------------------------------------------------------------------------

async function resizeImageToBlob(base64DataUrl, size) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      canvas.getContext('2d').drawImage(img, 0, 0, size, size);
      canvas.toBlob(resolve, 'image/png');
    };
    img.src = base64DataUrl;
  });
}

async function uploadKioskIcon(croppedBase64, deviceId) {
  const [blob192, blob512] = await Promise.all([
    resizeImageToBlob(croppedBase64, 192),
    resizeImageToBlob(croppedBase64, 512),
  ]);
  const ts = Date.now();

  // Upload 192
  const { data: d192, error: e192 } = await supabase.functions.invoke('generate-upload-url', {
    body: { action: 'upload', fileName: `${deviceId}-192-${ts}.png`, contentType: 'image/png', folderPath: 'kiosk-icons' },
  });
  if (e192) throw e192;
  const r192 = await fetch(d192.signedUrl, { method: 'PUT', headers: { 'Content-Type': 'image/png' }, body: blob192 });
  if (!r192.ok) throw new Error('Failed to upload 192px icon');

  // Upload 512
  const { data: d512, error: e512 } = await supabase.functions.invoke('generate-upload-url', {
    body: { action: 'upload', fileName: `${deviceId}-512-${ts}.png`, contentType: 'image/png', folderPath: 'kiosk-icons' },
  });
  if (e512) throw e512;
  const r512 = await fetch(d512.signedUrl, { method: 'PUT', headers: { 'Content-Type': 'image/png' }, body: blob512 });
  if (!r512.ok) throw new Error('Failed to upload 512px icon');

  return { icon_192_url: d192.publicUrl, icon_512_url: d512.publicUrl };
}

// ---------------------------------------------------------------------------
// CountdownTimer (for pairing dialog)
// ---------------------------------------------------------------------------

function CountdownTimer({ expiresAt, onExpire }) {
  const [remaining, setRemaining] = useState(null);

  useEffect(() => {
    if (!expiresAt) return;

    const calculateRemaining = () => {
      const diffMs = new Date(expiresAt) - new Date();
      if (diffMs <= 0) {
        setRemaining(0);
        onExpire?.();
        return false;
      }
      setRemaining(diffMs);
      return true;
    };

    const stillValid = calculateRemaining();
    if (!stillValid) return;

    const interval = setInterval(() => {
      if (!calculateRemaining()) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, onExpire]);

  if (remaining === null) return null;
  if (remaining <= 0) {
    return <span className="text-sm text-red-600 font-medium">Code expired</span>;
  }

  const mins = Math.floor(remaining / 60000);
  const secs = Math.floor((remaining % 60000) / 1000);

  return (
    <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <Clock className="h-3.5 w-3.5" />
      <span>Expires in {mins}:{secs.toString().padStart(2, '0')}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// KioskOverviewCard — summary card shown in the grid
// ---------------------------------------------------------------------------

function KioskOverviewCard({ device, onClick, onDelete }) {
  const statusBadge = getStatusBadge(device.status);
  const isOnline =
    device.status === 'active' &&
    device.last_heartbeat &&
    Date.now() - new Date(device.last_heartbeat).getTime() < 10 * 60 * 1000;

  return (
    <button
      onClick={() => onClick(device)}
      className="text-left w-full rounded-xl border bg-card hover:border-primary/30 hover:shadow-md transition-all group"
    >
      <div className="p-5">
        {/* Top row: icon + status */}
        <div className="flex items-start justify-between mb-3">
          {device.settings?.icon_192_url ? (
            <img
              src={device.settings.icon_192_url}
              alt={device.device_name || 'Kiosk'}
              className="h-10 w-10 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600 group-hover:bg-blue-100 transition-colors">
              <Monitor className="h-5 w-5" />
            </div>
          )}
          <Badge className={statusBadge.className + ' text-xs'}>
            {statusBadge.label}
          </Badge>
        </div>

        {/* Device name */}
        <h3 className="font-semibold text-sm text-foreground mb-1 truncate">
          {device.device_name || 'Kiosk'}
        </h3>

        {/* Meta row */}
        <div className="flex items-center gap-3 text-xs text-muted-foreground">
          {device.status === 'active' && (
            <>
              <span className="flex items-center gap-1">
                {isOnline ? (
                  <Wifi className="h-3 w-3 text-green-500" />
                ) : (
                  <WifiOff className="h-3 w-3 text-slate-400" />
                )}
                {formatLastSeen(device.last_heartbeat)}
              </span>
              <span className="truncate">{getAuthModeLabel(device.settings)}</span>
            </>
          )}
          {device.status === 'pending' && <span>Waiting for pairing...</span>}
          {device.status === 'revoked' && <span>Device revoked</span>}
        </div>
      </div>

      {/* Footer */}
      <div className="border-t px-5 py-2.5 flex items-center justify-between">
        <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
          {device.status === 'active' ? 'View settings' : 'View details'}
        </span>
        <div className="flex items-center gap-1">
          {device.status === 'revoked' && onDelete && (
            <span
              role="button"
              onClick={(e) => { e.stopPropagation(); onDelete(device); }}
              className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-600 transition-colors"
              title="Delete device"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </span>
          )}
          <ArrowUpRight className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary transition-colors" />
        </div>
      </div>
    </button>
  );
}

// ---------------------------------------------------------------------------
// KioskDetailView — full settings for a single kiosk
// ---------------------------------------------------------------------------

function KioskDetailView({ device, onBack, onUpdate, onRevoke, onDelete, projectId, companyId }) {
  const [editingName, setEditingName] = useState(false);
  const [editName, setEditName] = useState(device.device_name || '');
  const [saving, setSaving] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const inputRef = useRef(null);

  // Appearance state
  const [cropperOpen, setCropperOpen] = useState(false);
  const [cropperImage, setCropperImage] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const iconInputRef = useRef(null);

  const kioskUrl = device.device_token
    ? `${window.location.origin}/kiosk/${device.device_token}`
    : null;

  const currentAuthMode = getAuthMode(device.settings);

  useEffect(() => {
    if (editingName && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingName]);

  const handleSaveName = async () => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === device.device_name) {
      setEditName(device.device_name || 'Kiosk');
      setEditingName(false);
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('kiosk_devices')
        .update({ device_name: trimmed })
        .eq('id', device.id);
      if (error) throw error;
      toast.success('Device renamed');
      onUpdate();
    } catch (err) {
      console.error('Failed to rename device:', err);
      toast.error('Failed to rename device');
    } finally {
      setSaving(false);
      setEditingName(false);
    }
  };

  const handleAuthModeChange = async (mode) => {
    const newSettings = { ...(device.settings || {}) };
    switch (mode) {
      case 'pin_only':
        newSettings.require_pin = true;
        newSettings.allow_name_search = false;
        break;
      case 'search_only':
        newSettings.require_pin = false;
        newSettings.allow_name_search = true;
        break;
      default:
        newSettings.require_pin = true;
        newSettings.allow_name_search = true;
        break;
    }

    try {
      const { error } = await supabase
        .from('kiosk_devices')
        .update({ settings: newSettings })
        .eq('id', device.id);
      if (error) throw error;
      toast.success('Setting updated');
      onUpdate();
    } catch (err) {
      console.error('Failed to update settings:', err);
      toast.error('Failed to update settings');
    }
  };

  const handleToggleSelfie = async (checked) => {
    const newSettings = { ...(device.settings || {}), require_selfie: checked };
    try {
      const { error } = await supabase
        .from('kiosk_devices')
        .update({ settings: newSettings })
        .eq('id', device.id);
      if (error) throw error;
      toast.success('Setting updated');
      onUpdate();
    } catch (err) {
      console.error('Failed to update settings:', err);
      toast.error('Failed to update settings');
    }
  };

  const handleCopyUrl = async () => {
    if (!kioskUrl) return;
    try {
      await navigator.clipboard.writeText(kioskUrl);
      setUrlCopied(true);
      toast.success('Kiosk URL copied');
      setTimeout(() => setUrlCopied(false), 2000);
    } catch {
      toast.error('Failed to copy URL');
    }
  };

  // --- Appearance handlers ---

  const handleIconFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setCropperImage(reader.result);
      setCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleIconCropped = async (croppedBase64) => {
    setUploadingIcon(true);
    try {
      const urls = await uploadKioskIcon(croppedBase64, device.id);
      const newSettings = { ...(device.settings || {}), ...urls };
      const { error } = await supabase
        .from('kiosk_devices')
        .update({ settings: newSettings })
        .eq('id', device.id);
      if (error) throw error;
      toast.success('Kiosk icon updated');
      onUpdate();
    } catch (err) {
      console.error('Failed to upload kiosk icon:', err);
      toast.error('Failed to upload icon');
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleRemoveIcon = async () => {
    try {
      const newSettings = { ...(device.settings || {}) };
      delete newSettings.icon_192_url;
      delete newSettings.icon_512_url;
      const { error } = await supabase
        .from('kiosk_devices')
        .update({ settings: newSettings })
        .eq('id', device.id);
      if (error) throw error;
      toast.success('Icon removed');
      onUpdate();
    } catch (err) {
      console.error('Failed to remove icon:', err);
      toast.error('Failed to remove icon');
    }
  };

  const iconUrl = device.settings?.icon_192_url;

  const statusBadge = getStatusBadge(device.status);
  const isOnline =
    device.status === 'active' &&
    device.last_heartbeat &&
    Date.now() - new Date(device.last_heartbeat).getTime() < 10 * 60 * 1000;

  return (
    <div className="container mx-auto px-6 py-6 max-w-3xl">
      {/* Back + title */}
      <div className="mb-6">
        <button
          onClick={onBack}
          className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3 transition-colors"
        >
          <ChevronLeft className="h-4 w-4" />
          Back to Kiosks
        </button>

        <div className="flex items-center gap-3">
          {device.settings?.icon_192_url ? (
            <img
              src={device.settings.icon_192_url}
              alt={device.device_name || 'Kiosk'}
              className="h-10 w-10 rounded-lg object-cover"
            />
          ) : (
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
              <Monitor className="h-5 w-5" />
            </div>
          )}
          <div className="flex-1 min-w-0">
            {editingName ? (
              <div className="flex items-center gap-2">
                <Input
                  ref={inputRef}
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                  onBlur={handleSaveName}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); handleSaveName(); }
                    if (e.key === 'Escape') { setEditName(device.device_name || 'Kiosk'); setEditingName(false); }
                  }}
                  className="h-8 text-lg font-bold max-w-xs"
                  disabled={saving}
                />
                {saving && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold tracking-tight truncate">
                  {device.device_name || 'Kiosk'}
                </h2>
                {device.status === 'active' && (
                  <button
                    onClick={() => { setEditName(device.device_name || 'Kiosk'); setEditingName(true); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    title="Rename device"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-2 mt-0.5">
              <Badge className={statusBadge.className + ' text-xs'}>
                {statusBadge.label}
              </Badge>
              {device.status === 'active' && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground">
                  {isOnline ? (
                    <Wifi className="h-3 w-3 text-green-500" />
                  ) : (
                    <WifiOff className="h-3 w-3 text-slate-400" />
                  )}
                  Last seen: {formatLastSeen(device.last_heartbeat)}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {device.status !== 'active' ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Monitor className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
            <p className="font-medium text-muted-foreground">
              {device.status === 'pending'
                ? 'This device is waiting to be paired.'
                : 'This device has been revoked.'}
            </p>
            <Button
              variant="destructive"
              size="sm"
              className="mt-4"
              onClick={() => onDelete(device)}
            >
              <Trash2 className="h-3.5 w-3.5" />
              Delete Device
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {/* Kiosk URL */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <QrCode className="h-4 w-4 text-blue-600" />
                Kiosk URL
              </CardTitle>
            </CardHeader>
            <CardContent>
              {kioskUrl && (
                <div className="flex items-center gap-2 bg-muted/50 rounded-lg px-3 py-2">
                  <span className="text-sm text-muted-foreground truncate font-mono flex-1">
                    {kioskUrl}
                  </span>
                  <Button variant="ghost" size="sm" className="shrink-0 h-7" onClick={handleCopyUrl}>
                    {urlCopied ? (
                      <Check className="h-3.5 w-3.5 text-green-600" />
                    ) : (
                      <Copy className="h-3.5 w-3.5" />
                    )}
                    {urlCopied ? 'Copied' : 'Copy'}
                  </Button>
                  <Button variant="ghost" size="sm" className="shrink-0 h-7" asChild>
                    <a href={kioskUrl} target="_blank" rel="noopener noreferrer">
                      <ArrowUpRight className="h-3.5 w-3.5" />
                      Open
                    </a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Appearance */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Palette className="h-4 w-4 text-blue-600" />
                Appearance
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-5">
              {/* Icon + names row */}
              <div className="flex gap-5">
                {/* Icon preview + actions */}
                <div className="flex flex-col items-center gap-2">
                  <div className="w-20 h-20 rounded-xl border-2 border-dashed border-muted-foreground/25 flex items-center justify-center bg-muted/30 overflow-hidden">
                    {iconUrl ? (
                      <img src={iconUrl} alt="Kiosk icon" className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      <Monitor className="h-8 w-8 text-muted-foreground/40" />
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs px-2"
                      onClick={() => iconInputRef.current?.click()}
                      disabled={uploadingIcon}
                    >
                      {uploadingIcon ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Upload className="h-3 w-3" />
                      )}
                      {iconUrl ? 'Change' : 'Upload'}
                    </Button>
                    {iconUrl && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 text-xs px-2 text-muted-foreground"
                        onClick={handleRemoveIcon}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    )}
                  </div>
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleIconFileSelect}
                  />
                </div>

                {/* Kiosk Name */}
                <div className="flex-1 flex flex-col justify-center">
                  <Label className="text-xs text-muted-foreground">Kiosk Name</Label>
                  <p className="text-sm font-medium mt-0.5">{device.device_name || 'Kiosk'}</p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Shown under the app icon on the home screen. Edit via the pencil icon above.
                  </p>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Changes take effect the next time the kiosk app is installed or refreshed.
              </p>
            </CardContent>
          </Card>

          {/* ImageCropper dialog */}
          <ImageCropper
            isOpen={cropperOpen}
            onClose={() => { setCropperOpen(false); setCropperImage(null); }}
            imageSrc={cropperImage}
            onCropComplete={handleIconCropped}
            aspectRatio={1}
            cropShape="rect"
          />

          {/* Device Settings */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Settings2 className="h-4 w-4 text-blue-600" />
                Device Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auth mode */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm font-medium">Login Method</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    How employees identify themselves on this kiosk
                  </p>
                </div>
                <Select value={currentAuthMode} onValueChange={handleAuthModeChange}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AUTH_MODES.map((mode) => (
                      <SelectItem key={mode.value} value={mode.value}>
                        {mode.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Selfie */}
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label className="text-sm font-medium flex items-center gap-1.5">
                    <Camera className="h-3.5 w-3.5 text-muted-foreground" />
                    Require Selfie
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Employees must take a selfie when clocking in
                  </p>
                </div>
                <Switch
                  checked={!!device.settings?.require_selfie}
                  onCheckedChange={handleToggleSelfie}
                />
              </div>
            </CardContent>
          </Card>

          {/* Data Import */}
          <KioskDataImport projectId={projectId} companyId={companyId} />

          {/* Danger Zone */}
          <Card className="border-red-200">
            <CardHeader className="pb-3">
              <CardTitle className="text-base text-red-600 flex items-center gap-2">
                <Trash2 className="h-4 w-4" />
                Danger Zone
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium">Revoke this kiosk</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    The device will no longer be able to process check-ins and will need to be re-paired.
                  </p>
                </div>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRevoke(device)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Revoke
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KiosksView — main exported component
// ---------------------------------------------------------------------------

export default function KiosksView({ siteLocationId, projectId, companyId }) {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDevice, setSelectedDevice] = useState(null);

  // Pairing state
  const [pairing, setPairing] = useState(false);
  const [pairingStep, setPairingStep] = useState('setup'); // 'setup' | 'code'
  const [pairingName, setPairingName] = useState('');
  const [pairingIconUrls, setPairingIconUrls] = useState(null); // { icon_192_url, icon_512_url } or null
  const [pairingCropperOpen, setPairingCropperOpen] = useState(false);
  const [pairingCropperImage, setPairingCropperImage] = useState(null);
  const [pairingUploadingIcon, setPairingUploadingIcon] = useState(false);
  const [pairingCode, setPairingCode] = useState(null);
  const [pairingExpiresAt, setPairingExpiresAt] = useState(null);
  const [pairingExpired, setPairingExpired] = useState(false);
  const [pairingDeviceId, setPairingDeviceId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);
  const pairingIconInputRef = useRef(null);

  // Revoke state
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [revoking, setRevoking] = useState(false);

  // Delete state
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const PAIRING_URL = `${window.location.origin}/kiosk/pair`;

  // ---- Data Fetching ----

  const fetchDevices = useCallback(async () => {
    if (!siteLocationId) return;
    try {
      const { data, error } = await supabase
        .from('kiosk_devices')
        .select('*')
        .eq('site_location_id', siteLocationId)
        .neq('status', 'expired')
        .order('created_date', { ascending: false });
      if (error) throw error;
      setDevices(data || []);

      // If we have a selected device, refresh it
      if (selectedDevice) {
        const updated = (data || []).find((d) => d.id === selectedDevice.id);
        if (updated) setSelectedDevice(updated);
      }
    } catch (err) {
      console.error('Failed to fetch kiosk devices:', err);
      toast.error('Failed to load kiosk devices');
    } finally {
      setLoading(false);
    }
  }, [siteLocationId, selectedDevice?.id]);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  // Poll for pairing status
  useEffect(() => {
    if (!pairing || !pairingDeviceId || pairingExpired) return;

    const interval = setInterval(async () => {
      const { data } = await supabase
        .from('kiosk_devices')
        .select('status')
        .eq('id', pairingDeviceId)
        .single();

      if (data?.status === 'active') {
        toast.success('Kiosk device paired successfully!');
        setPairing(false);
        resetPairingState();
        fetchDevices();
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [pairing, pairingDeviceId, pairingExpired, fetchDevices]);

  // ---- Pairing Flow ----

  const resetPairingState = () => {
    setPairingStep('setup');
    setPairingName('');
    setPairingIconUrls(null);
    setPairingCropperOpen(false);
    setPairingCropperImage(null);
    setPairingUploadingIcon(false);
    setPairingCode(null);
    setPairingExpiresAt(null);
    setPairingExpired(false);
    setPairingDeviceId(null);
    setCopied(false);
  };

  // Open the pairing dialog (starts at setup step)
  const handleOpenPairingDialog = () => {
    resetPairingState();
    setPairing(true);
  };

  // Icon upload handlers for the pairing setup step
  const handlePairingIconSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      setPairingCropperImage(reader.result);
      setPairingCropperOpen(true);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handlePairingIconCropped = async (croppedBase64) => {
    setPairingUploadingIcon(true);
    try {
      // Upload using a temporary ID (we don't have a device ID yet)
      const tempId = `new-${Date.now()}`;
      const urls = await uploadKioskIcon(croppedBase64, tempId);
      setPairingIconUrls(urls);
      toast.success('Icon uploaded');
    } catch (err) {
      console.error('Failed to upload icon:', err);
      toast.error('Failed to upload icon');
    } finally {
      setPairingUploadingIcon(false);
    }
  };

  // Generate the pairing code (step 2) — creates the device row
  const handleGeneratePairingCode = async () => {
    const name = pairingName.trim();
    if (!name) {
      toast.error('Please enter a kiosk name');
      return;
    }

    setCreating(true);
    setPairingExpired(false);
    setCopied(false);

    try {
      const code = generatePairingCode();
      const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();

      const insertData = {
        company_id: companyId,
        site_location_id: siteLocationId,
        project_id: projectId,
        pairing_code: code,
        pairing_expires_at: expiresAt,
        status: 'pending',
        device_name: name,
      };

      // Include icon URLs in settings if an icon was uploaded
      if (pairingIconUrls) {
        insertData.settings = {
          theme: 'light',
          show_clock: true,
          require_pin: true,
          allow_name_search: true,
          show_who_is_on_site: true,
          idle_timeout_seconds: 30,
          ...pairingIconUrls,
        };
      }

      const { data, error } = await supabase
        .from('kiosk_devices')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      setPairingCode(code);
      setPairingExpiresAt(expiresAt);
      setPairingDeviceId(data.id);
      setPairingStep('code');
    } catch (err) {
      console.error('Failed to create pairing code:', err);
      toast.error('Failed to generate pairing code');
    } finally {
      setCreating(false);
    }
  };

  // Regenerate expired code (reuses existing name/icon)
  const handleRegeneratePairingCode = async () => {
    if (!pairingDeviceId) return;
    setCreating(true);
    setPairingExpired(false);
    setCopied(false);

    try {
      const code = generatePairingCode();
      const expiresAt = new Date(Date.now() + 5 * 60 * 1000).toISOString();

      const { error } = await supabase
        .from('kiosk_devices')
        .update({
          pairing_code: code,
          pairing_expires_at: expiresAt,
        })
        .eq('id', pairingDeviceId);

      if (error) throw error;

      setPairingCode(code);
      setPairingExpiresAt(expiresAt);
    } catch (err) {
      console.error('Failed to regenerate pairing code:', err);
      toast.error('Failed to generate new code');
    } finally {
      setCreating(false);
    }
  };

  const handlePairingExpired = useCallback(() => {
    setPairingExpired(true);
  }, []);

  const handleClosePairing = () => {
    setPairing(false);
    resetPairingState();
    fetchDevices();
  };

  const handleCopyCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setCopied(true);
      toast.success('Pairing code copied');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy code');
    }
  };

  // ---- Revoke ----

  const handleRevokeDevice = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      const { error } = await supabase
        .from('kiosk_devices')
        .update({ status: 'revoked' })
        .eq('id', revokeTarget.id);
      if (error) throw error;
      toast.success(`${revokeTarget.device_name || 'Device'} has been revoked`);
      setRevokeTarget(null);
      // If we were viewing this device, go back to overview
      if (selectedDevice?.id === revokeTarget.id) {
        setSelectedDevice(null);
      }
      fetchDevices();
    } catch (err) {
      console.error('Failed to revoke device:', err);
      toast.error('Failed to revoke device');
    } finally {
      setRevoking(false);
    }
  };

  // ---- Delete ----

  const handleDeleteDevice = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const { error } = await supabase
        .from('kiosk_devices')
        .delete()
        .eq('id', deleteTarget.id);
      if (error) throw error;
      toast.success(`${deleteTarget.device_name || 'Device'} has been deleted`);
      setDeleteTarget(null);
      if (selectedDevice?.id === deleteTarget.id) {
        setSelectedDevice(null);
      }
      fetchDevices();
    } catch (err) {
      console.error('Failed to delete device:', err);
      toast.error('Failed to delete device');
    } finally {
      setDeleting(false);
    }
  };

  // ---- No site location fallback ----

  if (!siteLocationId) {
    return (
      <div className="container mx-auto px-6 py-6 max-w-4xl">
        <div className="mb-6">
          <h2 className="text-2xl font-bold tracking-tight">Kiosks</h2>
          <p className="text-muted-foreground mt-1">Manage kiosk devices for this site</p>
        </div>
        <Card>
          <CardContent className="py-12 text-center">
            <Monitor className="h-12 w-12 mx-auto mb-3 text-muted-foreground/40" />
            <p className="font-medium text-muted-foreground">No site location configured</p>
            <p className="text-sm text-muted-foreground mt-1">
              Create a site location in Settings to enable kiosk devices.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Detail view ----

  if (selectedDevice) {
    return (
      <>
        <KioskDetailView
          device={selectedDevice}
          onBack={() => setSelectedDevice(null)}
          onUpdate={fetchDevices}
          onRevoke={setRevokeTarget}
          onDelete={setDeleteTarget}
          projectId={projectId}
          companyId={companyId}
        />

        {/* Revoke Confirmation Dialog */}
        <Dialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Revoke Kiosk Device</DialogTitle>
              <DialogDescription>
                Are you sure you want to revoke{' '}
                <span className="font-medium text-foreground">
                  {revokeTarget?.device_name || 'this device'}
                </span>
                ? It will no longer be able to process check-ins and will need to be re-paired.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setRevokeTarget(null)} disabled={revoking}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleRevokeDevice} disabled={revoking}>
                {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Revoke Device
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle>Delete Kiosk Device</DialogTitle>
              <DialogDescription>
                Are you sure you want to permanently delete{' '}
                <span className="font-medium text-foreground">
                  {deleteTarget?.device_name || 'this device'}
                </span>
                ? This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDeleteDevice} disabled={deleting}>
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                Delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // ---- Overview ----

  const activeDevices = devices.filter((d) => d.status === 'active');
  const otherDevices = devices.filter((d) => d.status !== 'active');

  return (
    <>
      <div className="container mx-auto px-6 py-6 max-w-4xl">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Kiosks</h2>
            <p className="text-muted-foreground mt-1">
              Manage kiosk devices for tablet-based check-ins
            </p>
          </div>
          <Button onClick={handleOpenPairingDialog}>
            <Plus className="h-4 w-4" />
            Pair New Kiosk
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : devices.length === 0 ? (
          <Card>
            <CardContent className="py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted mx-auto mb-4">
                <Monitor className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="font-medium text-foreground mb-1">No kiosk devices</p>
              <p className="text-sm text-muted-foreground mb-4">
                Pair a kiosk device to enable tablet-based check-ins at this site.
              </p>
              <Button onClick={handleStartPairing} disabled={creating}>
                {creating ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4" />
                )}
                Pair New Kiosk
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Active devices grid */}
            {activeDevices.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-muted-foreground mb-3">
                  Active ({activeDevices.length})
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activeDevices.map((device) => (
                    <KioskOverviewCard
                      key={device.id}
                      device={device}
                      onClick={setSelectedDevice}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Revoked / pending devices — collapsed by default */}
            {otherDevices.length > 0 && (
              <Collapsible>
                <CollapsibleTrigger className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors group">
                  <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=closed]:-rotate-90" />
                  Revoked ({otherDevices.length})
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-3">
                    {otherDevices.map((device) => (
                      <KioskOverviewCard
                        key={device.id}
                        device={device}
                        onClick={setSelectedDevice}
                        onDelete={setDeleteTarget}
                      />
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>
            )}
          </div>
        )}
      </div>

      {/* Pairing Dialog (two-step: setup → code) */}
      <Dialog open={pairing} onOpenChange={(open) => !open && handleClosePairing()}>
        <DialogContent className="sm:max-w-md">
          {pairingStep === 'setup' ? (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Monitor className="h-5 w-5" />
                  Set Up Kiosk
                </DialogTitle>
                <DialogDescription>
                  Give this kiosk a name and optionally upload an icon before pairing.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-5 py-2">
                {/* Kiosk Name */}
                <div className="space-y-2">
                  <Label htmlFor="pairing-name">Kiosk Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="pairing-name"
                    value={pairingName}
                    onChange={(e) => setPairingName(e.target.value)}
                    placeholder="e.g. Reception Kiosk"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && pairingName.trim()) {
                        e.preventDefault();
                        handleGeneratePairingCode();
                      }
                    }}
                  />
                  <p className="text-xs text-muted-foreground">
                    This name appears under the app icon on the home screen.
                  </p>
                </div>

                {/* Icon Upload */}
                <div className="space-y-2">
                  <Label>App Icon <span className="text-xs text-muted-foreground font-normal">(optional)</span></Label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-xl border-2 border-dashed border-muted-foreground/25 flex items-center justify-center bg-muted/30 overflow-hidden shrink-0">
                      {pairingIconUrls ? (
                        <img src={pairingIconUrls.icon_192_url} alt="Icon preview" className="w-full h-full object-cover rounded-xl" />
                      ) : (
                        <ImageIcon className="h-6 w-6 text-muted-foreground/40" />
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs"
                        onClick={() => pairingIconInputRef.current?.click()}
                        disabled={pairingUploadingIcon}
                      >
                        {pairingUploadingIcon ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Upload className="h-3 w-3" />
                        )}
                        {pairingIconUrls ? 'Change Icon' : 'Upload Icon'}
                      </Button>
                      {pairingIconUrls && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs text-muted-foreground"
                          onClick={() => setPairingIconUrls(null)}
                        >
                          <X className="h-3 w-3" />
                          Remove
                        </Button>
                      )}
                    </div>
                    <input
                      ref={pairingIconInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={handlePairingIconSelect}
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="gap-2 sm:gap-0">
                <Button variant="outline" onClick={handleClosePairing}>
                  Cancel
                </Button>
                <Button onClick={handleGeneratePairingCode} disabled={creating || !pairingName.trim()}>
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
                  Generate Pairing Code
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <QrCode className="h-5 w-5" />
                  Pair &ldquo;{pairingName}&rdquo;
                </DialogTitle>
                <DialogDescription>
                  Open the pairing page on the kiosk device and enter this code, or scan the QR code below.
                </DialogDescription>
              </DialogHeader>

              <div className="flex flex-col items-center gap-5 py-2">
                <div className="rounded-lg border bg-white p-4">
                  <QRCodeSVG value={PAIRING_URL} size={160} level="M" />
                </div>

                {pairingExpired ? (
                  <div className="flex flex-col items-center gap-3">
                    <p className="text-sm text-red-600 font-medium">Code expired</p>
                    <Button variant="outline" size="sm" onClick={handleRegeneratePairingCode} disabled={creating}>
                      {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                      Generate New Code
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-3xl font-bold tracking-[0.3em] select-all">
                        {pairingCode}
                      </span>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleCopyCode} title="Copy code">
                        {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
                      </Button>
                    </div>
                    <CountdownTimer expiresAt={pairingExpiresAt} onExpire={handlePairingExpired} />
                  </div>
                )}

                <p className="text-xs text-muted-foreground text-center">
                  Or navigate to{' '}
                  <span className="font-mono text-xs bg-muted px-1.5 py-0.5 rounded">{PAIRING_URL}</span>
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={handleClosePairing}>
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* ImageCropper for pairing icon upload */}
      <ImageCropper
        isOpen={pairingCropperOpen}
        onClose={() => { setPairingCropperOpen(false); setPairingCropperImage(null); }}
        imageSrc={pairingCropperImage}
        onCropComplete={handlePairingIconCropped}
        aspectRatio={1}
        cropShape="rect"
      />

      {/* Revoke Confirmation Dialog (from overview) */}
      <Dialog open={!!revokeTarget} onOpenChange={(open) => !open && setRevokeTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Revoke Kiosk Device</DialogTitle>
            <DialogDescription>
              Are you sure you want to revoke{' '}
              <span className="font-medium text-foreground">
                {revokeTarget?.device_name || 'this device'}
              </span>
              ? It will no longer be able to process check-ins and will need to be re-paired.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevokeTarget(null)} disabled={revoking}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleRevokeDevice} disabled={revoking}>
              {revoking ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Revoke Device
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog (from overview) */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Kiosk Device</DialogTitle>
            <DialogDescription>
              Are you sure you want to permanently delete{' '}
              <span className="font-medium text-foreground">
                {deleteTarget?.device_name || 'this device'}
              </span>
              ? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteDevice} disabled={deleting}>
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
